import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, UtensilsCrossed, ArrowUpRight } from "lucide-react";

const Industries = () => {
  const industries = [
    {
      tag: "Edutech",
      icon: GraduationCap,
      title: "Smart campuses, end to end.",
      copy: "We power leading schools and academies with EdKonect — our integrated campus management platform — and bespoke engineering for everything around it.",
      image:
        "https://images.unsplash.com/photo-1774600787713-bc405e935202?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzgxMDY1NDg0fDA&ixlib=rb-4.1.0&q=85",
      meta: "100K+ students • 5,000+ teachers • Multi-state rollout",
      testid: "industry-edutech",
    },
    {
      tag: "Food-tech",
      icon: UtensilsCrossed,
      title: "Operations engineered for scale.",
      copy: "Long-term technology partner to Isthara — India's No.1 multi-brand dining platform. We build the systems that keep 1M+ orders moving across hundreds of vendors.",
      image:
        "https://images.unsplash.com/photo-1663790776711-9283bf614ac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXN5JTIwcmVzdGF1cmFudCUyMGtpdGNoZW58ZW58MHx8fHwxNzgxMDY1NDgxfDA&ixlib=rb-4.1.0&q=85",
      meta: "1M+ orders • 200+ vendors • 60% workload reduction",
      testid: "industry-foodtech",
    },
  ];

  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="font-mono text-xs text-[#0038FF]">[ 03 ]</span>
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0038FF]">
            Industry Focus
          </span>
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="industries-heading"
          className="font-display text-[2.4rem] sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0a0a0a] max-w-3xl mb-16 break-words"
        >
          Two industries.
          <br />
          One operating discipline.
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {industries.map((ind, idx) => {
            const Icon = ind.icon;
            return (
              <motion.article
                key={ind.tag}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.8,
                  delay: idx * 0.12,
                  ease: [0.16, 1, 0.3, 1],
                }}
                data-testid={ind.testid}
                className="group relative overflow-hidden bg-[#0a0a0a] min-h-[520px] flex flex-col justify-end"
              >
                <img
                  src={ind.image}
                  alt={ind.tag}
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 industry-overlay" />

                <div className="absolute top-8 left-8 right-8 flex items-start justify-between">
                  <div className="flex items-center gap-3 bg-white px-4 py-2">
                    <Icon className="w-4 h-4 text-[#0038FF]" strokeWidth={2.25} />
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#0a0a0a]">
                      {ind.tag}
                    </span>
                  </div>
                  <div className="w-11 h-11 border border-white/40 text-white flex items-center justify-center backdrop-blur-sm group-hover:bg-[#0038FF] group-hover:border-[#0038FF] transition-colors">
                    <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
                  </div>
                </div>

                <div className="relative z-10 p-8 lg:p-12 text-white">
                  <h3 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-4">
                    {ind.title}
                  </h3>
                  <p className="text-white/85 text-base lg:text-lg leading-relaxed max-w-md">
                    {ind.copy}
                  </p>
                  <div className="mt-8 pt-6 border-t border-white/20 text-xs font-mono uppercase tracking-[0.18em] text-white/70">
                    {ind.meta}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Industries;
